import { BackendHttpService } from "./backendHttpService";

export class ExportService extends BackendHttpService {
  constructor() {
    super("export");
  }

  async exportToWordTemplate(userId: number, language: string = 'None'): Promise<Blob> {
    try {
      const response = await fetch(
        this.buildUrl(`?id=${userId}&template=Default&targetLanguage=${language}`),
        {
          method: "GET",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.blob();
    } catch (error) {
      console.error("Error exporting to Word template:", error);
      throw error;
    }
  }

  async exportToWordWithoutNames(userId: number, language: string = 'None'): Promise<Blob> {
    try {
      const response = await fetch(
        this.buildUrl(`?id=${userId}&template=WithoutNames&targetLanguage=${language}`),
        {
          method: "GET",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.blob();
    } catch (error) {
      console.error("Error exporting to Word without names:", error);
      throw error;
    }
  }

  async exportToWord(userId: number, language: string = 'None', template: 'Default' | 'WithoutNames' = 'Default'): Promise<Blob> {
    try {
      const response = await fetch(
        this.buildUrl(`?id=${userId}&template=${template}&targetLanguage=${language}`),
        {
          method: "GET",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to export CV as Word document`;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch {
          errorMessage = `HTTP error! Status: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();

      if (blob.type === 'application/json') {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || errorData.error || 'Failed to export CV');
        } catch {
          throw new Error('Server returned an error response');
        }
      }

      return blob;
    } catch (error) {
      console.error("Error exporting to Word:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to export CV as Word document. Please try again.");
    }
  }

  async exportToPdf(userId: number, language: string = 'None', documentTemplate: 'Default' | 'WithoutNames' = 'Default'): Promise<Blob> {
    try {
      const response = await fetch(
        this.buildUrl(`pdf?id=${userId}&targetLanguage=${language}&documentTemplate=${documentTemplate}`),
        {
          method: "GET",
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to export CV as PDF document`;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch {
          // If we can't parse the error, use the status
          errorMessage = `HTTP error! Status: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();

      if (blob.type === 'application/json') {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || errorData.error || 'Failed to export CV');
        } catch {
          throw new Error('Server returned an error response');
        }
      }

      return blob;
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to export CV as PDF document. Please try again.");
    }
  }

  async exportMultipleToWordTemplate(userIds: number[]): Promise<Blob> {
    try {
      const response = await fetch(this.buildUrl("multiple"), {
        method: "POST",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userIds, template: "Default" }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.blob();
    } catch (error) {
      console.error("Error exporting multiple to Word template:", error);
      throw error;
    }
  }

  async exportMultipleToWordWithoutNames(userIds: number[]): Promise<Blob> {
    try {
      const response = await fetch(this.buildUrl("multiple"), {
        method: "POST",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userIds, template: "WithoutNames" }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.blob();
    } catch (error) {
      console.error("Error exporting multiple to Word without names:", error);
      throw error;
    }
  }

  async exportMultipleToPdf(userIds: number[]): Promise<Blob> {
    try {
      const response = await fetch(this.buildUrl("multiplePdf"), {
        method: "POST",
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userIds }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.blob();
    } catch (error) {
      console.error("Error exporting multiple to PDF:", error);
      throw error;
    }
  }

  async bulkExportByIds(
    personIds: number[],
    template: 'Default' | 'WithoutNames' = 'Default',
    targetLanguage: string = 'None',
    format: 'Docx' | 'Pdf' | 'Europass' = 'Docx'
  ): Promise<Blob> {
    const EuropassFormat = 3;
    const PdfFormat = 2;
    const DocxFormat = 1;
    const formatValue = format === 'Europass' ? EuropassFormat : (format === 'Pdf' ? PdfFormat : DocxFormat);

    const response = await fetch(
      this.buildUrl(`bulk-export-by-ids?template=${template}&targetLanguage=${targetLanguage}&format=${formatValue}`),
      {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(personIds),
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to bulk export selected CVs';
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.Error || errorData.error || errorData.title || errorMessage;
          } catch {
            errorMessage = errorText;
          }
        }
      } catch {
        errorMessage = `HTTP error! Status: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const blob = await response.blob();
    if (blob.type === 'application/json') {
      const text = await blob.text();
      try {
        const errorData = JSON.parse(text);
        throw new Error(errorData.Error || errorData.error || 'Failed to bulk export selected CVs');
      } catch {
        throw new Error('Server returned an error response');
      }
    }

    return blob;
  }

  async bulkExport(
    searchRequest: any,
    template: 'Default' | 'WithoutNames' = 'Default',
    targetLanguage: string = 'None',
    format: 'Docx' | 'Pdf' | 'Europass' = 'Docx'
  ): Promise<Blob> {
    try {
      const EuropassFormat = 3;
      const PdfFormat = 2;
      const DocxFormat = 1;

      const formatValue = format === 'Europass' ? EuropassFormat : (format === 'Pdf' ? PdfFormat : DocxFormat);
      const response = await fetch(
        this.buildUrl(`bulk-export?template=${template}&targetLanguage=${targetLanguage}&format=${formatValue}`),
        {
          method: "POST",
          headers: await this.getAuthHeaders(),
          body: JSON.stringify(searchRequest),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to bulk export CVs`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.Error || errorData.error || errorData.title || errorMessage;

              if (errorData.errors) {
                const validationErrors = Object.entries(errorData.errors)
                  .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                  .join('; ');
                errorMessage += ` - Validation errors: ${validationErrors}`;
              }
            } catch (parseError) {
              errorMessage = errorText;
            }
          }
        } catch (textError) {
          errorMessage = `HTTP error! Status: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();

      if (blob.type === 'application/json') {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.Error || errorData.error || 'Failed to bulk export CVs');
        } catch {
          throw new Error('Server returned an error response');
        }
      }

      return blob;
    } catch (error) {
      console.error("Error bulk exporting CVs:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to bulk export CVs. Please try again.");
    }
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const accessToken = await this.getAccessToken();

    return {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
  }
}
